// ** react imports
import React, { useState, useEffect } from 'react';

// ** toaster imports
import { toast } from 'react-toastify';

// ** hooks
import {
  useDeleteNotification,
  useSendBroadcastNotification,
} from '../../../hooks/apis/useNotification';

// ** components
import ModalBasic from '../../../components/ModalBasic';
import Loading from '../../../components/common/Loading';
import NotificationTableItem from './NotificationTableItem';

const NotificationTable = props => {
  // props
  const { data, isLoading, currentSortBy, onSort, refetch } = props;

  // states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [broadcastModalOpen, setBroadcastModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToBroadcast, setItemToBroadcast] = useState(null);
  const [broadcastedNotifications, setBroadcastedNotifications] = useState(
    new Set()
  );

  // handlers
  const handleOpenDeleteModal = item => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleOpenBroadcastModal = item => {
    setItemToBroadcast(item);
    setBroadcastModalOpen(true);
  };

  const { deleteNotification, isDeleteNotificationLoading } =
    useDeleteNotification();

  const { sendBroadcastNotification, isSendBroadcastNotificationLoading } =
    useSendBroadcastNotification();

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      const response = await deleteNotification(itemToDelete._id);

      if (response.error) {
        throw new Error(
          response.error?.data?.message || 'Failed to delete notification'
        );
      }

      setDeleteModalOpen(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleBroadcastConfirm = async () => {
    if (!itemToBroadcast) return;
    try {
      const response = await sendBroadcastNotification(itemToBroadcast);

      if (response.error) {
        throw new Error(
          response.error?.data?.message || 'Failed to broadcast notification'
        );
      }

      setBroadcastedNotifications(prev =>
        new Set(prev).add(itemToBroadcast._id)
      );
      setBroadcastModalOpen(false);
      setItemToBroadcast(null);
      refetch();
    } catch (error) {
      toast.error(
        error.message || 'An error occurred while broadcasting the notification'
      );
    }
  };
  // Clear broadcasted notifications when data changes (after refetch)
  useEffect(() => {
    setBroadcastedNotifications(new Set());
  }, [data]);

  const SortIcon = ({ field }) => {
    if (currentSortBy !== field) {
      return (
        <svg
          className="w-3 h-3 ml-1 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return currentSortBy === field && currentSortBy === 'asc' ? (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 ml-1 text-violet-60 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handleSortClick = field => {
    if (onSort) {
      onSort(field);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 h-[calc(70vh)] shadow-xs rounded-xl relative overflow-y-auto">
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center gap-3 text-gray-60 dark:text-gray-300">
            <Loading />
          </div>
        </div>
      )}

      <div>
        <header className="px-4 sm:px-5 py-4 bg-white dark:bg-gray-800 sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              All Notifications{' '}
              <span className="text-gray-40 dark:text-gray-500 font-medium">
                {data?.total || 0}
              </span>
            </h2>
          </div>
        </header>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700/60">
            <thead className="sticky top-0 z-10 text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/20 border-t border-b border-gray-100 dark:border-gray-700/60">
              <tr>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[180px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('title')}
                    aria-label="Sort by title"
                  >
                    Title
                    <SortIcon field="title" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[210px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('content')}
                    aria-label="Sort by content"
                  >
                    Content
                    <SortIcon field="content" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('priority')}
                    aria-label="Sort by priority"
                  >
                    Priority
                    <SortIcon field="priority" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px] text-center"
                >
                  <button
                    className="font-semibold text-center whitespace-nowrap uppercase flex items-center justify-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('isRead')}
                    aria-label="Sort by status"
                  >
                    Status
                    <SortIcon field="isRead" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[150px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('createdAt')}
                    aria-label="Sort by date"
                  >
                    Date
                    <SortIcon field="createdAt" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[80px] max-w-[100px] text-center"
                >
                  <div className="font-semibold text-center">Actions</div>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-700/60">
              {data?.data && data.data.length > 0 ? (
                data.data.map(notification => (
                  <NotificationTableItem
                    key={notification._id}
                    data={notification}
                    viewMode="table"
                    onOpenDeleteModal={handleOpenDeleteModal}
                    onOpenBroadcastModal={handleOpenBroadcastModal}
                    isBroadcasted={broadcastedNotifications.has(
                      notification._id
                    )}
                  />
                ))
              ) : (
                <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M18 10C18 6.68629 15.3137 4 12 4C8.68629 4 6 6.68629 6 10V18H18V10ZM20 18.6667L20.4 19.2C20.5657 19.4209 20.5209 19.7343 20.3 19.9C20.2135 19.9649 20.1082 20 20 20H4C3.72386 20 3.5 19.7761 3.5 19.5C3.5 19.3918 3.53509 19.2865 3.6 19.2L4 18.6667V10C4 5.58172 7.58172 2 12 2C16.4183 2 20 5.58172 20 10V18.6667ZM9.5 21H14.5C14.5 22.3807 13.3807 23.5 12 23.5C10.6193 23.5 9.5 22.3807 9.5 21Z"></path>
                        </svg>
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        No Notifications found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Try adjusting your filters or search criteria to find
                        what you&apos;re looking for.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        id={`delete-modal-${itemToDelete?._id}`}
        modalOpen={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        title="Delete Notification"
      >
        <div className="px-5 pt-4 pb-1">
          <div className="text-sm mb-4">
            <p className="text-gray-60 dark:text-gray-300">
              Are you sure you want to delete the notification{' '}
              <strong>{itemToDelete?.title}</strong>?
            </p>
            <p className="text-gray-600 dark:text-gray-30 mt-2">
              This action cannot be undone. The notification will be permanently
              removed from the system.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60 flex justify-end space-x-3">
          <button
            className="btn px-4 py-2 rounded-lg border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => setDeleteModalOpen(false)}
            disabled={isDeleteNotificationLoading}
          >
            Cancel
          </button>
          <button
            className="btn px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center"
            onClick={handleDeleteConfirm}
            disabled={isDeleteNotificationLoading}
          >
            {isDeleteNotificationLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </ModalBasic>

      {/* Broadcast Confirmation Modal */}
      <ModalBasic
        id={`broadcast-modal-${itemToBroadcast?._id}`}
        modalOpen={broadcastModalOpen}
        setModalOpen={setBroadcastModalOpen}
        title="Broadcast Notification"
      >
        <div className="px-5 pt-4 pb-1">
          <div className="text-sm mb-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to broadcast the notification{' '}
              <strong>{itemToBroadcast?.title}</strong>?
            </p>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              This action will send the notification to all users. This action
              cannot be undone.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60 flex justify-end space-x-3">
          <button
            className="btn px-4 py-2 rounded-lg border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => setBroadcastModalOpen(false)}
            disabled={isSendBroadcastNotificationLoading}
          >
            Cancel
          </button>
          <button
            className="btn px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white transition-colors flex items-center"
            onClick={handleBroadcastConfirm}
            disabled={isSendBroadcastNotificationLoading}
          >
            {isSendBroadcastNotificationLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Broadcasting...
              </>
            ) : (
              'Broadcast'
            )}
          </button>
        </div>
      </ModalBasic>
    </div>
  );
};

export default NotificationTable;
